import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RenderTableOrders from "./renderTable";
import EditOrders from "./editOrders";
import { socket } from "../../utils/socket";
import { useNotification } from "../../context/NotificationContext";
import ConfirmDialog from "../../components/ConfirmDialog";

const ORDERS_PER_PAGE = 20;

export default function Orders({ userData }) {
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  const navigate = useNavigate();
  const sessionExpiredRef = useRef(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [aggregatorFilter, setAggregatorFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [allOrders, setAllOrders] = useState([]);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [serverTotalItems, setServerTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // ✅ Fetch all orders
  const fetchOrders = useCallback(async () => {
    if (sessionExpiredRef.current) return;
    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        page: currentPage,
        limit: ORDERS_PER_PAGE,
      });
      if (searchTerm) params.append("search", searchTerm);
      if (aggregatorFilter) params.append("aggregator", aggregatorFilter);

      const res = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/orders?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const resdata = await res.json();

      if (!res.ok || !resdata.success) {
        if (res.status === 401) {
          showNotificationRef.current(
            "Session expired. Please login again.",
            "error"
          );
          localStorage.removeItem("token");
          sessionExpiredRef.current = true;
          navigate("/login", { replace: true });
          return;
        }
      }

      if (resdata?.data) {
        // Server returns page items + pagination info
        setAllOrders(resdata.data);
        setServerTotalPages(resdata.pagination?.totalPages || 1);
        setServerTotalItems(
          resdata.pagination?.totalItems ?? resdata.data.length ?? 0
        );
      } else {
        throw new Error("Invalid orders data format");
      }
    } catch (err) {
      console.error("Error loading orders:", err);
      showNotificationRef.current("Error loading orders", "error");
      setError(err.message);
      setAllOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, navigate, searchTerm, aggregatorFilter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // ✅ Play notification sound
  const playSound = useCallback(() => {
    const audio = new Audio("/assets/audio/notifyneworder.mp3");
    audio.play().catch(() => {});
  }, []);

  // ✅ Listen for real-time new orders
  const handleNewOrder = useCallback(
    (newOrder) => {
      console.log("📦 New order received via Socket.IO:", newOrder);
      playSound();
      showNotificationRef.current("New order received", "success");

      const orderId = newOrder._id || newOrder.id;

      setAllOrders((prev) => {
        const exists = prev.some(
          (o) =>
            o._id === orderId ||
            o.id === orderId ||
            o.orderId === newOrder.orderId
        );
        if (exists) return prev;
        return [newOrder, ...prev];
      });
    },
    [playSound]
  );

  useEffect(() => {
    socket.on("newOrder", handleNewOrder);
    return () => socket.off("newOrder", handleNewOrder);
  }, [handleNewOrder]);

  useEffect(() => {
    showNotificationRef.current = showNotification;
  }, [showNotification]);

  // Server-side paginated results: `allOrders` already contains current page items
  const paginatedOrders = allOrders;
  const totalPages = serverTotalPages;

  // ✅ Pagination handlers
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleAggregatorChange = (agg) => {
    setAggregatorFilter(agg);
    setCurrentPage(1);
  };

  // ✅ Edit handlers
  const handleEdit = (order) => {
    setOrderToEdit(order);
    setIsFormVisible(true);
  };

  const handleCancel = () => {
    setIsFormVisible(false);
    setTimeout(() => setOrderToEdit(null), 300);
  };

  const handleUpdate = async (payload) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/orders/${payload._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);

      const updatedOrder = await response.json();
      setAllOrders(
        allOrders.map((order) =>
          order._id === updatedOrder._id ? updatedOrder : order
        )
      );

      showNotification("Order updated successfully", "success");
      handleCancel();
    } catch (error) {
      console.error("Error updating order:", error);
      showNotification("Failed to update order", "error");
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ Delete order handlers
  const handleDelete = (orderId) => {
    setDeleteId(orderId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_BACKENDORDERURL}/orders/${deleteId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error(`HTTP error: ${response.status}`);

      setAllOrders((prev) => prev.filter((order) => order._id !== deleteId));
      showNotification("Order deleted successfully", "success");
    } catch (error) {
      console.error("Error deleting order:", error);
      showNotification("Failed to delete order", "error");
    } finally {
      setConfirmOpen(false);
      setDeleteId(null);
      setIsLoading(false);
    }
  };

  return (
    <section id="orders-section" className="content-body">
      {error && <p style={{ color: "red" }}>Error: {error}</p>}

      {!error && allOrders && (
        <RenderTableOrders
          paginatedOrders={paginatedOrders}
          userData={userData}
          allOrders={allOrders}
          onSearch={handleSearch}
          onAggregatorChange={handleAggregatorChange}
          searchValue={searchTerm}
          aggregatorValue={aggregatorFilter}
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={serverTotalItems}
          itemsPerPage={ORDERS_PER_PAGE}
          handlePageChange={handlePageChange}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
        />
      )}

      {orderToEdit && (
        <div
          className={`modal-backdrop ${isFormVisible ? "show" : ""}`}
          onClick={handleCancel}
        >
          <div
            className="order-form-container"
            onClick={(e) => e.stopPropagation()}
          >
            <EditOrders
              order={orderToEdit}
              onSubmit={handleUpdate}
              onCancel={handleCancel}
            />
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Order?"
        message="Are you sure you want to delete this order? This action cannot be undone."
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmDelete}
      />
    </section>
  );
}
